import React from 'react';
import Button from 'components/Button';
import { IconType } from 'components/Icon/constants';
import { EditorKeyBindingCommand } from 'const';
import * as AppModels from 'containers/App/models';
import { useIsOpenToolbar } from 'hooks/useIsOpenToolbar';
import { undoRedoKeyBindings } from 'hooks/useUndoRedo/utils';
import { IAction } from 'models';
import { eventEmitter, EMITTER_EVENTS } from 'utils/eventEmitter';
import styles from './styles.module.scss';

type Props = {
  isRedoDisabled: boolean;
  isUndoDisabled: boolean;
  startHandlingReusableLayoutsEditing?: AppModels.ActionCreator.StartHandlingReusableLayoutsEditing;
  stopHandlingReusableLayoutsEditing?: AppModels.ActionCreator.StopHandlingReusableLayoutsEditing;
  undo: () => void;
  redo: () => void;
};

const UndoRedoControl: React.FunctionComponent<Props> = ({
  redo,
  undo,
  isRedoDisabled,
  isUndoDisabled,
  stopHandlingReusableLayoutsEditing = (): null => null,
  startHandlingReusableLayoutsEditing = (): null => null,
}) => {
  const undoWithLayoutEditing = (): IAction | void => {
    stopHandlingReusableLayoutsEditing();
    const result = undo();
    startHandlingReusableLayoutsEditing();

    return result;
  };
  const isOpenToolbar = useIsOpenToolbar();

  React.useEffect(() => {
    if (isOpenToolbar) {
      return () => {};
    }

    const undoRedoGlobalHandler = (e: KeyboardEvent): void => {
      const command = undoRedoKeyBindings(e);
      if (command === EditorKeyBindingCommand.UNDO && !isUndoDisabled) {
        undoWithLayoutEditing();
      }

      if (command === EditorKeyBindingCommand.REDO && !isRedoDisabled) {
        redo();
      }
    };

    eventEmitter.addListener(EMITTER_EVENTS.BODY_KEYDOWN, undoRedoGlobalHandler);

    return () => {
      eventEmitter.removeListener(EMITTER_EVENTS.BODY_KEYDOWN, undoRedoGlobalHandler);
    };
  }, [isOpenToolbar, isUndoDisabled, isRedoDisabled]);

  return (
    <div className={styles.UndoRedoControl}>
      <Button
        icon={IconType.UNDO}
        size="md"
        disabled={isUndoDisabled}
        onClick={undoWithLayoutEditing}
        highlightOnHover={true}
      />
      <Button
        icon={IconType.REDO}
        size="md"
        disabled={isRedoDisabled}
        onClick={redo}
        highlightOnHover={true}
      />
    </div>
  );
};

export default UndoRedoControl;
